import { t } from 'i18next';
import RowRepeatedEmail from 'components/Common/FormBuilder/RowRepeatedEmail/RowRepeatedEmail';
import RowRepeatedPassword from 'components/Common/FormBuilder/RowRepeatedPassword/RowRepeatedPassword';
import RowAddressBuilder from 'components/Common/FormBuilder/RowAddress/RowAddressBuilder';
import RowNewsletterBuilder from 'components/Identification/Signup/SignupForm/Newsletter/RowNewsletterBuilder';
import RowMobileNumberBuilder from 'components/Identification/Signup/SignupForm/MobileNumber/RowMobileNumberBuilder';
import RowDateInputsBuilder from 'components/Common/FormBuilder/RowDateInputs/RowDateInputsBuilder';
import { genderOptions, genderOptionsUk } from 'model/CommonFields';
import { isFr, isUk } from 'services/currentCountry';
import { AC } from 'settings/form';
import { Field } from 'types/Field';
import { FIELDS_REGEX } from '../regex';

type OrderedField = Field & { order: number };

const isFrVar = isFr();
const isUkVar = isUk();
const addressesFields: Field[] = [
  {
    type: 'text',
    name: 'nameWay',
    label: t('LABEL_NAME_WAY', { ns: 'signUpPage' }),
    placeholder: t('LABEL_NAME_WAY_PLACEHOLDER', { ns: 'signUpPage' }),
    required: true,
    autoComplete: AC.StreetAddress,
  },
];

if (isFrVar) {
  addressesFields.push({
    type: 'text',
    name: 'nameComplement',
    label: t('LABEL_BUILDING_COMPLEMENT', { ns: 'signUpPage' }),
    placeholder: t('LABEL_BUILDING_COMPLEMENT_PLACEHOLDER', {
      ns: 'signUpPage',
    }),
    autoComplete: AC.Off,
  });
}

addressesFields.push(
  {
    type: 'text',
    name: 'nameAddress',
    label: t('LABEL_BUILDING', { ns: 'signUpPage' }),
    placeholder: t('LABEL_BUILDING_PLACEHOLDER', { ns: 'signUpPage' }),
    autoComplete: AC.Off,
  },
  {
    type: 'text',
    name: 'locality',
    label: t('LABEL_LOCALITY', { ns: 'signUpPage' }),
    placeholder: t('LABEL_LOCALITY_PLACEHOLDER', { ns: 'signUpPage' }),
    autoComplete: AC.Off,
  }
);

const newsletterOptions = [
  {
    label: t('LABEL_YES', { ns: 'signUpPage' }),
    value: 'O',
  },
  {
    label: t('LABEL_NO', { ns: 'signUpPage' }),
    value: 'N',
  },
];

export const signUpFields: OrderedField[] = [
  {
    type: 'repeated',
    repeatedType: 'email',
    name: 'email',
    secondName: 'confirmEmail',
    label: t('LABEL_EMAIL_ADDRESS', { ns: 'signUpPage' }),
    placeholder: t('LABEL_EMAIL_PLACEHOLDER', { ns: 'signUpPage' }),
    secondLabel: t('LABEL_CONFIRM_EMAIL_ADDRESS', { ns: 'signUpPage' }),
    secondPlaceholder: t('LABEL_CONFIRM_EMAIL_PLACEHOLDER', {
      ns: 'signUpPage',
    }),
    required: true,
    order: 1,
    inputProps: {
      model: 'secondary',
      autoCapitalize: 'off',
    },
    component: RowRepeatedEmail,
    emailAlreadyUsedMessage: t('LABEL_ERROR_EMAIL_ALREADY_TAKEN', {
      ns: 'signUpPage',
    }),
  },
  {
    type: 'repeated',
    repeatedType: 'password',
    name: 'password',
    secondName: 'confirmPassword',
    label: t('LABEL_PASSWORD_ACCOUNT_CREATE', { ns: 'signUpPage' }),
    placeholder: t('LABEL_PASSWORD_PLACEHOLDER', { ns: 'signUpPage' }),
    secondLabel: t('LABEL_CONFIRM_PASSWORD', { ns: 'signUpPage' }),
    secondPlaceholder: t('LABEL_CONFIRM_PASSWORD_PLACEHOLDER', {
      ns: 'signUpPage',
    }),
    autoComplete: AC.Off,
    required: true,
    order: 2,
    inputProps: {
      model: 'secondary',
    },
    component: RowRepeatedPassword,
  },
  {
    type: 'radio',
    name: 'civility',
    options: isUkVar ? genderOptionsUk : genderOptions,
    required: true,
    autoComplete: AC.HonorificPrefix,
    order: 3,
  },
  {
    type: 'text',
    name: 'surname',
    label: t('LABEL_SURNAME', { ns: 'translation' }),
    placeholder: t('LABEL_SURNAME_PLACEHOLDER', { ns: 'translation' }),
    autoComplete: AC.LastName,
    required: true,
    order: isFrVar ? 5 : 4,
  },
  {
    type: 'text',
    name: 'firstName',
    label: t('LABEL_FIRST_NAME', { ns: 'translation' }),
    placeholder: t('LABEL_FIRST_NAME_PLACEHOLDER', { ns: 'translation' }),
    autoComplete: AC.FirstName,
    required: true,
    order: isFrVar ? 4 : 5,
  },
  {
    type: 'date',
    name: 'dateOfBirth',
    label: t('LABEL_DATE_OF_BIRTH', { ns: 'signUpPage' }),
    dayProps: { autoComplete: AC.BirthDay },
    monthProps: { autoComplete: AC.BirthMonth },
    yearProps: { autoComplete: AC.BirthYear },
    order: 6,
    component: RowDateInputsBuilder,
  },
  {
    name: 'address',
    fields: addressesFields,
    order: 7,
    component: RowAddressBuilder,
  },
  {
    type: 'text',
    name: 'postCode',
    label: t('LABEL_POSTCODE', { ns: 'signUpPage' }),
    placeholder: t('LABEL_POSTCODE_PLACEHOLDER', { ns: 'signUpPage' }),
    required: true,
    autoComplete: AC.PostalCode,
    order: 8,
    onChange: (callback: (value: string) => void, inputVal: string): void => {
      if (isUkVar) {
        callback(inputVal.toUpperCase());

        return;
      }

      if (isFrVar && FIELDS_REGEX['number'].test(inputVal)) {
        callback(inputVal);
      }
    },
  },
  {
    type: 'text',
    name: 'city',
    label: t('LABEL_CITY', { ns: 'signUpPage' }),
    placeholder: t('LABEL_CITY_PLACEHOLDER', { ns: 'signUpPage' }),
    required: true,
    autoComplete: AC.City,
    order: 9,
  },
  {
    type: 'text',
    name: 'country',
    required: true,
    disabled: true,
    autoComplete: AC.Country,
    order: 10,
    classes: styles => ({
      inputContainer: styles.countryInputContainer,
      input: styles.countryInput,
    }),
  },
  {
    type: 'tel',
    name: 'landLine',
    label: t('LABEL_LANDLINE', { ns: 'signUpPage' }),
    placeholder: t('LABEL_LANDLINE_PLACEHOLDER', { ns: 'signUpPage' }),
    autoComplete: AC.Landline,
    onChange: (callback: (value: string) => void, inputVal: string): void => {
      if (FIELDS_REGEX['number'].test(inputVal)) {
        callback(inputVal);
      }
    },
    order: 12,
  },
  {
    type: 'tel',
    name: 'mobileNumber',
    label: t('LABEL_PHONE_NUMBER', { ns: 'signUpPage' }),
    placeholder: t('LABEL_PHONE_NUMBER_PLACEHOLDER', { ns: 'signUpPage' }),
    autoComplete: AC.Off,
    onChange: (callback: (value: string) => void, inputVal: string): void => {
      if (FIELDS_REGEX['number'].test(inputVal)) {
        callback(inputVal);
      }
    },
    order: 11,
    optInSmsName: 'newsAfibelSms',
    component: RowMobileNumberBuilder,
  },
  {
    name: 'newsletter',
    fields: [
      {
        type: 'radio',
        name: 'newsAfibelMail',
        options: newsletterOptions,
        required: true,
      },
      {
        type: 'radio',
        name: 'newsAfibelSms',
        options: newsletterOptions,
      },
      {
        name: 'newsPartnerAfibelMail',
        type: 'radio',
        options: newsletterOptions,
        required: true,
      },
    ],
    order: 13,
    component: RowNewsletterBuilder,
  },
];
